import {TypedEventTarget} from '../../logics/TypedEventTarget';
import {HEL_AUDIO_TYPE_VOICE} from '../../logics/const';
import {getMixerMuteAt, getMixerSettingAt} from '../../logics/dataOps';
import {ensureScriptLoaded} from '../../logics/externalScriptLoader';
import {getLocalStream, closeLocalStream, setLocalStream} from '../../logics/localStream';
import {parseJwt} from '../../logics/parseJwt';
import {setMicEnabled} from '../../logics/shared';
import {GetIHeliNetwork} from '../../singleton_registry';
import {
  onNetReceiveVoiceJoin,
  onNetReceiveVoiceLeave,
  onNetVoiceConnected,
  onNetVoiceDisconnected
} from '../networkCallback';

import type {
  IHeliVoiceChannelProvider,
  VoiceChannelProviderEventMap
} from './IHeliVoiceChannelProvider';
import type {
  SMCNWConnectOption,
  SMCNWLocalTrackPublication,
  SMCNWRemoteTrackPublication
} from '../../types/internal';
import type {ChannelSession, PositionChannelDetail} from '../../types/network';
import type {IHeliMic, IHeliRtcTokenRoomTarget, Transform} from '../CHeliNetwork';
import type {CHeliPlayer} from '../CHeliPlayer';
import type {PositionChannelProviderEventMap} from '../positionChannelProviders/IHeliPositionChannelProvider';

declare global {
  /** @remarks 本APIの型定義は公開されておらず、ドキュメントの閲覧権限もCarnelianSmcn開発者のみに限られる。 */
  /* eslint-disable no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須 */
  var SMCNWClient: Record<string, any> | undefined;
  /* eslint-enable */
}

/** 型定義が不明なクラスに対するインスタンスを表す型。 */
type AnyClassInstance = Record<string, any>;

/** ネットワークで使用するTransform情報のリクエスト */
const onNetRequestCurrentLocalTransform = (): void => {
  // @ts-expect-error 2025-10以降、Smcn使用予定なし
  hel_script_CallCanvasComponentMethod(
    'vcc_icon_only_base',
    'VCCViewModel',
    'RequestCurrentLocalTransform',
    'undefined'
  );
};

/**
 * ブラウザコンソールからのデバッグのために、グローバル変数へ保存する。
 */
const registerSmcnClientAsGlobalVariableForDebug = (smcnClient: AnyClassInstance) => {
  Object.assign(globalThis, {smcnClient});
};

export class CHeliSmcnVoiceChannelProvider
  extends TypedEventTarget<VoiceChannelProviderEventMap>
  implements IHeliVoiceChannelProvider
{
  /** @remarks 本APIの型定義は公開されておらず、ドキュメントの閲覧権限もCarnelianSmcn開発者のみに限られる。 */
  #smcnClient: AnyClassInstance | null = null;

  #smcnWLocalTrackPublication: SMCNWLocalTrackPublication | null = null;
  #smcnWRemoteTrackPublication: SMCNWRemoteTrackPublication | null = null;
  #smcnWSpeakerRemoteTrackPublication: SMCNWRemoteTrackPublication | null = null;
  #smcnWMediaTrackSettings = {
    inputMute: true,
    outputEnable: true
  };

  // 頻度軽減のため
  #currentUserPosition = {
    x: 0,
    y: 0,
    z: 0
  };

  #currentUserOrientation = {
    x: 0.0,
    y: 0.0,
    z: 0.0,
    w: 0.0
  };

  #userPosition = {
    x: 0,
    y: 0,
    z: 0
  };

  #userOrientation = {
    x: 0.0,
    y: 0.0,
    z: 0.0,
    w: 0.0
  };

  #globalPlayerVoiceVolume = getMixerSettingAt(HEL_AUDIO_TYPE_VOICE);

  async ensureLibraryLoadedAndInitialized(): Promise<void> {
    await ensureScriptLoaded('lib/smcn/massive-connection-network-client.umd.js');
  }

  prepareForNewJoin(): boolean {
    return true;
  }

  onRemoteJoin(): void {}

  onRemoteLeave(detail: PositionChannelProviderEventMap['remoteleave']['detail']): void {}

  connectVoiceChannel(): void {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    if (!globalThis.SMCNWClient) {
      throw new Error('SMCNWClient is not defined');
    }

    const client = new globalThis.SMCNWClient.SMCNWClient();
    console.debug('init smcnClient', client);

    //
    // callbacks
    //

    // サーバに接続(デフォルトグループに接続後もこちら)
    const handleConnected = async (tenantId: string, worldId: string, identity: string) => {
      console.debug(`Connected tenantId::${tenantId} worldId::${worldId} identity::${identity}`);
      this.#updateVoiceChannelMember();

      const userPosition = {
        x: this.#userPosition.x,
        y: this.#userPosition.y,
        z: this.#userPosition.z
      };
      const userOrientation = {
        x: this.#userOrientation.x,
        y: this.#userOrientation.y,
        z: this.#userOrientation.z,
        w: this.#userOrientation.w
      };

      client.setUserTransform(userPosition, userOrientation);
    };

    // サーバから切断
    const handleDisconnected = () => {
      console.debug('Disconnected');
    };

    // 接続している音声機器の変更
    const handleMediaDevicesChanged = () => {
      console.debug('MediaDevicesChanged');
    };

    // 同一グループ内にユーザが接続
    const handleParticipantConnected = (identity: string) => {
      console.debug('ParticipantConnected', identity);

      const player = heliNetwork.findPlayerByRtcUserCode(Number.parseInt(identity));
      if (!player) {
        return;
      }

      // 音声チャンネルへのJoinを通知
      onNetReceiveVoiceJoin(player.getPlayerId(), '');
    };

    // 同一グループ内からユーザが切断
    const handleParticipantDisconnected = (identity: string) => {
      console.debug('ParticipantDisconnected', identity);

      const player = heliNetwork.findPlayerByRtcUserCode(Number.parseInt(identity));
      if (!player) {
        return;
      }

      // 音声チャンネルからのLeaveを通知
      onNetReceiveVoiceLeave(player.getPlayerId(), '');
    };

    // ローカルトラックパブリッシュ後
    const handleLocalTrackPublished = async (localPub: SMCNWLocalTrackPublication) => {
      console.debug('localTrackPublished', localPub, this.#smcnWMediaTrackSettings.inputMute);
      localPub.setMute(this.#smcnWMediaTrackSettings.inputMute);
      if (!this.#smcnWMediaTrackSettings.inputMute) {
        // detectのmaxDomainが0になるため、止めてから再開する
        setMicEnabled(false);
        setMicEnabled(true);
      }
      this.#smcnWLocalTrackPublication = localPub;
    };

    const handleLocalTrackUnpublished = () => {
      console.debug('localTrackUnpublished');
      this.#smcnWLocalTrackPublication = null;
    };

    // クラウドレンダリング用コンポーネントの接続後
    const handleProxyConnected = () => {
      console.debug('Proxy Connected');
    };

    // 再生する音声ストリームがパブリッシュ後
    const handleProxyTrackPublished = (remotePub: SMCNWRemoteTrackPublication) => {
      console.debug('ProxyTrackPublished', remotePub);
      remotePub.setEnabled(this.#smcnWMediaTrackSettings.outputEnable);
    };

    // Speakerモードの再生する音声ストリームがパブリッシュ後
    const handleProxySpeakerTrackPublished = (remotePub: SMCNWRemoteTrackPublication) => {
      console.debug('ProxySpeakerTrackPublished', remotePub);
      remotePub.setEnabled(this.#smcnWMediaTrackSettings.outputEnable);
    };

    // 再生する音声ストリームの取得に成功後
    const handleProxyTrackSubscribed = (
      remotePub: SMCNWRemoteTrackPublication,
      audioElements: HTMLAudioElement[]
    ) => {
      console.debug('ProxyTrackSubscribed', remotePub, audioElements);
      this.#smcnWRemoteTrackPublication = remotePub;
      this.#applyRemoteVolume();
    };

    // Speakerモードの再生する音声ストリームの取得に成功後
    const handleProxySpeakerTrackSubscribed = (
      remotePub: SMCNWRemoteTrackPublication,
      audioElements: HTMLAudioElement[]
    ) => {
      console.debug('ProxySpeakerTrackSubscribed', remotePub, audioElements);
      this.#smcnWSpeakerRemoteTrackPublication = remotePub;
      this.#applyRemoteVolume();
    };

    const handleProxyTrackUnsubscribed = (remotePub: SMCNWRemoteTrackPublication) => {
      console.debug('ProxyTrackUnsubscribed', remotePub);
    };

    const handleProxySpeakerTrackUnsubscribed = (remotePub: SMCNWRemoteTrackPublication) => {
      console.debug('ProxySpeakerTrackUnsubscribed', remotePub);
    };

    // クラウドレンダリング用コンポーネントの切断後
    const handleProxyDisconnected = () => {
      console.debug('Proxy Disconnected');

      // SDKの更新により再接続機能の追加予定
      if (
        window.confirm(
          'SmcnClientクラウドレンダリングとの接続が切断されました、復帰のためにはページをリロードしてください'
        )
      ) {
        window.location.reload();
      }
    };

    client.on(globalThis.SMCNWClient.SMCNWEvent.Connected, handleConnected);
    client.on(globalThis.SMCNWClient.SMCNWEvent.Disconnected, handleDisconnected);
    client.on(globalThis.SMCNWClient.SMCNWEvent.LocalTrackPublished, handleLocalTrackPublished);
    client.on(globalThis.SMCNWClient.SMCNWEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);
    client.on(globalThis.SMCNWClient.SMCNWEvent.MediaDevicesChanged, handleMediaDevicesChanged);
    client.on(globalThis.SMCNWClient.SMCNWEvent.ProxyConnected, handleProxyConnected);
    client.on(globalThis.SMCNWClient.SMCNWEvent.ProxyDisconnected, handleProxyDisconnected);
    client.on(globalThis.SMCNWClient.SMCNWEvent.ProxyTrackPublished, handleProxyTrackPublished);
    client.on(
      globalThis.SMCNWClient.SMCNWEvent.ProxySpeakerTrackPublished,
      handleProxySpeakerTrackPublished
    );
    client.on(globalThis.SMCNWClient.SMCNWEvent.ProxyTrackSubscribed, handleProxyTrackSubscribed);
    client.on(
      globalThis.SMCNWClient.SMCNWEvent.ProxySpeakerTrackSubscribed,
      handleProxySpeakerTrackSubscribed
    );
    client.on(
      globalThis.SMCNWClient.SMCNWEvent.ProxyTrackUnsubscribed,
      handleProxyTrackUnsubscribed
    );
    client.on(
      globalThis.SMCNWClient.SMCNWEvent.ProxySpeakerTrackUnsubscribed,
      handleProxySpeakerTrackUnsubscribed
    );

    client.on(globalThis.SMCNWClient.SMCNWEvent.ParticipantConnected, handleParticipantConnected);
    client.on(
      globalThis.SMCNWClient.SMCNWEvent.ParticipantDisconnected,
      handleParticipantDisconnected
    );

    // 定期的に座標を監視する
    const intervalSendUserPosition = window.setInterval(() => {
      try {
        if (!client.isConnected) {
          return;
        }
        onNetRequestCurrentLocalTransform();
        const userPosition = {
          x: this.#userPosition.x,
          y: this.#userPosition.y,
          z: this.#userPosition.z
        };
        const userOrientation = {
          x: this.#userOrientation.x,
          y: this.#userOrientation.y,
          z: this.#userOrientation.z,
          w: this.#userOrientation.w
        };
        // 変わってないなら送らない
        if (
          userPosition.x === this.#currentUserPosition.x &&
          userPosition.y === this.#currentUserPosition.y &&
          userPosition.z === this.#currentUserPosition.z &&
          userOrientation.x === this.#currentUserOrientation.x &&
          userOrientation.y === this.#currentUserOrientation.y &&
          userOrientation.z === this.#currentUserOrientation.z &&
          userOrientation.w === this.#currentUserOrientation.w
        ) {
          return;
        }
        client.setUserTransform(userPosition, userOrientation);
        this.#currentUserPosition = userPosition;
        this.#currentUserOrientation = userOrientation;
      } catch (error) {
        console.error(`[smcnClient] sendUserPosition: ${error}`);
      }
    }, 500);

    window.addEventListener('beforeunload', () => {
      try {
        client.disconnect();
      } catch (error) {
        console.error(`[smcnClient] beforeunload disconnect: ${error}`);
        window.alert(`[smcnClient] beforeunload disconnect: ${error}`);
      }
      window.clearInterval(intervalSendUserPosition);
    });

    window.addEventListener('pagehide', () => {
      try {
        client.disconnect();
      } catch (error) {
        console.error(`[smcnClient] pagehide disconnect: ${error}`);
        window.alert(`[smcnClient] pagehide disconnect: ${error}`);
      }
      window.clearInterval(intervalSendUserPosition);
    });

    this.#smcnClient = client;

    registerSmcnClientAsGlobalVariableForDebug(client);
  }

  // 現在Smcnに接続しているメンバーを表示
  #updateVoiceChannelMember() {
    // 一旦処理を待ってから行う
    window.setTimeout(async () => {
      if (!this.#smcnClient) {
        return;
      }

      const heliNetwork = GetIHeliNetwork();
      if (!heliNetwork) {
        return;
      }
      const memberidentities = await this.#smcnClient.getWorldMembers();
      for (const identity of memberidentities) {
        const player = heliNetwork.findPlayerByRtcUserCode(Number.parseInt(identity));
        if (!player) {
          continue;
        }

        // 音声チャンネルへのJoinを通知
        onNetReceiveVoiceJoin(player.getPlayerId(), '');
      }
    }, 5000);
  }

  onData(data: string, playerId: string): void {}

  disconnectFromVoiceChannel(): void {
    if (this.#smcnClient) {
      try {
        this.#smcnClient.disconnect();
      } catch (error) {
        console.error(`[smcnClient] CarnelianSmcn disconnect: ${error}`);
        window.alert(`[smcnClient] CarnelianSmcn disconnect: ${error}`);
      }
    }
  }

  async prepareLeavePositionChannel(): Promise<void> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    heliNetwork.setCurrentVoiceChannel(null);
    // 音声チャンネルからの退出を通知
    onNetVoiceDisconnected();
  }

  async postLeaveVoiceChannelCleanup(): Promise<void> {
    // Smcnに関してのグループという概念がなくなったため、一旦コメントアウト
    // this.#smcnLeaveGroup();

    // マイクをミュートする
    this.#smcnWMediaTrackSettings.inputMute = true;
    this.#smcnWLocalTrackPublication?.setMute(this.#smcnWMediaTrackSettings.inputMute);

    // 音声チャンネルからの退出を通知
    onNetVoiceDisconnected();
  }

  /**
   * グループから退室する
   * @remarks
   * 既にデフォルトグループの場合、退室処理はスキップされる
   */
  async #smcnLeaveGroup() {
    if (this.#smcnClient) {
      try {
        await this.#smcnClient.leaveGroup();
      } catch (error) {
        console.error(`[smcnClient] leaveGroup: ${error}`);
        window.alert(`[smcnClient] leaveGroup: ${error}`);
      }
    }
  }

  async onJoinPositionChannel(
    positionChannelDetail: PositionChannelDetail,
    channelSessionJwt: string
  ): Promise<void> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    if (!globalThis.SMCNWClient) {
      throw new Error('SMCNWClient is not defined');
    }

    if (!heliNetwork.micReady) {
      // デフォルトマイクデバイスをブラウザから取得
      const pIHeliMic = await heliNetwork.getMicDevice();
      await heliNetwork.initLocalMicInput(pIHeliMic?.deviceId ?? null);
      await heliNetwork.checkTrackState(pIHeliMic?.deviceId ?? null);
    }

    const type = 'smcn';
    const mode = 'talk';
    const roomTarget = 'position_channel_code';
    const disableRtcUser = false;
    const decodeJwt = parseJwt(channelSessionJwt) as ChannelSession;
    const returnValue = await globalThis.heliport.v3.api.genericRtcToken.getToken({
      channelSessionJwt,
      type,
      mode,
      disableRtcUser,
      roomTarget
    });
    if (returnValue.status !== 'ok') {
      console.error('iHeliNet joinPosChannel error:', returnValue);
      heliNetwork.setError({code: '400', data: returnValue});
      return;
    }
    const token = returnValue.data;

    const smcnToken = token.token;
    const smcnUserId = token.user_id;
    if (!this.#smcnClient) {
      console.error('iHeliNet joinPosChannel error: smcnClient is null');
      heliNetwork.setError({code: '400', data: 'smcnClient is null'});
      return;
    }
    if (smcnUserId) {
      // ユーザIDとワールドID（ポジションチャンネル別にするため、position_channel_codeとする）
      const option: SMCNWConnectOption = {
        worldId: decodeJwt.position_channel_code,
        identity: smcnUserId,
        mode:
          decodeJwt.user_role === 'presenter'
            ? globalThis.SMCNWClient.Mode.SPEAKER
            : globalThis.SMCNWClient.Mode.NORMAL,
        auth: {
          mcnToken: smcnToken
        }
      };
      await this.#smcnConnect(option);
    } else {
      this.#handleSmcnConnectError(
        'The user_id returned by genericRtcToken.getToken() is undefined.'
      );
    }
    await heliNetwork.checkMicPermissionState();
    onNetVoiceConnected(decodeJwt.channel_session_code ?? null);
  }
  async #smcnConnect(option: SMCNWConnectOption) {
    if (!this.#smcnClient) {
      this.#handleSmcnConnectError('smcnClient is null');
      return;
    }
    try {
      await this.#smcnClient.connect(option);
    } catch (error) {
      // 何らかの理由によりコネクションに失敗（大体は制限人数オーバーが原因）
      this.#handleSmcnConnectError(error);
    }
  }
  #handleSmcnConnectError(error: unknown) {
    console.error('[SmcnClient] Connect:', error);
    if (
      window.confirm(
        'SmcnClientクラウドレンダリングとの接続に失敗しました、復帰のためにはページをリロードしてください'
      )
    ) {
      window.location.reload();
    }
  }

  /** globalThis.SMCNWClient.Modeの定義は以下の通り
   *  enum Mode {
   *    NORMAL = 0,
   *    SPEAKER = 1
   *  }
   */
  setSpeakerMode(speakerMode: boolean) {
    if (this.#smcnClient) {
      if (this.#smcnClient.isConnected) {
        const mode = speakerMode ? 1 : 0;
        this.#smcnClient.changeMode(mode);
        return true;
      }
    }
    return false;
  }

  /** @remarks サーバサイド距離減衰をするので何もしない */
  initializePlayerVoice(player: CHeliPlayer): void {}
  onPlayerRemoved(player: CHeliPlayer): void {}
  /** @remarks サーバサイド距離減衰をするので何もしない */
  setPlayerVoiceStream(playerOrPlayerId: CHeliPlayer | string, stream: MediaStream): void {}
  #applyRemoteVolume() {
    // player個人のミュートもしくは自身の設定からミュートの場合はvolumeを0にする
    const isMuted = getMixerMuteAt(HEL_AUDIO_TYPE_VOICE);
    // そのうちplayer指定できるようにする
    // const mute = (player.mute || isMuted) ? true : false;
    const fixedVolume = this.#globalPlayerVoiceVolume * (isMuted ? 0.0 : 1.0);
    try {
      this.#smcnWRemoteTrackPublication?.setVolume(fixedVolume);
      this.#smcnWSpeakerRemoteTrackPublication?.setVolume(fixedVolume);
    } catch (err) {
      // trackがない場合でも、処理を継続する
      console.error(err);
    }
  }
  /**
   * @remarks {@link SMCNWRemoteTrackPublication} では、個別ミュートは未対応。
   * @see https://discord.com/channels/596169759045976094/1243065534916853930/1354283488248856708
   */
  applyPlayerVoiceMute(player: CHeliPlayer): void {
    const rtcUserCode = player.getRtcUserCode();
    if (!rtcUserCode) {
      // 既にいないため、リターン
      return;
    }
    void (async () => {
      try {
        await this.#smcnWRemoteTrackPublication?.setMute(`${rtcUserCode}`, player.mute);
      } catch (error) {
        // もしSmcn側で失敗しても処理は続ける
        console.error(error);
      }
    })();
  }
  /** @remarks サーバサイド距離減衰をするので何もしない */
  setPlayerVoiceAttenuate(player: CHeliPlayer): void {}
  setGlobalPlayerVoiceVolume(volume: number): void {
    this.#globalPlayerVoiceVolume = volume;
    this.#applyRemoteVolume();
  }

  /**
   * @inheritDoc
   * @remarks
   * {@link CHeliSmcnVoiceChannelProvider} は画面共有に対応していない。
   */
  async startScreenShareStream(useCamera: boolean, useAudio: boolean): Promise<MediaStream | null> {
    return null;
  }
  /**
   * @inheritDoc
   * @remarks
   * {@link CHeliSmcnVoiceChannelProvider} は画面共有に対応していない。
   */
  async cleanupScreenShareStream(): Promise<boolean> {
    return false;
  }
  onPlayScreenShare(): void {}
  useNotEmptyLocalStream(): boolean {
    return getLocalStream() ? true : false;
  }

  async setMicEnabled(enabled: boolean): Promise<void> {
    this.#smcnWMediaTrackSettings.inputMute = !enabled;
    if (this.#smcnWLocalTrackPublication) {
      try {
        this.#smcnWLocalTrackPublication.setMute(!enabled);
      } catch (err) {
        // trackがない場合でも、処理を継続する
        console.error(err);
      }
    } else {
      if (!this.#smcnClient) {
        throw new Error('smcnClient is null');
      }
      if (!this.#smcnClient.isConnected) {
        return;
      }
      const localStream = getLocalStream();
      if (localStream) {
        const track = localStream.getAudioTracks()[0];
        if (!track) {
          throw new Error('getLocalStream().getAudioTracks() returns an empty array.');
        }
        const localTrack = await this.#smcnClient.createLocalTrack(track);
        await this.#smcnClient.publishTrack(localTrack);
      }
    }
  }
  getMicEnabled(): boolean {
    if (!this.#smcnWLocalTrackPublication) {
      return false;
    }
    return !this.#smcnWLocalTrackPublication.isMuted;
  }
  supportsSpeakerMode(): boolean {
    return true;
  }

  setCurrentLocalTransform(param: {player: Transform; camera: Transform}): void {
    this.#userPosition.x = param.player.pos.x;
    this.#userPosition.y = param.player.pos.y;
    this.#userPosition.z = param.player.pos.z;
    this.#userOrientation.x = param.camera.rotate.x;
    this.#userOrientation.y = param.camera.rotate.y;
    this.#userOrientation.z = param.camera.rotate.z;
    this.#userOrientation.w = param.camera.rotate.w;
  }

  async joinVoiceRtc(
    pIHeliMic: IHeliMic | null,
    pIHeliRtcTokenRoomTarget: IHeliRtcTokenRoomTarget | null
  ): Promise<void> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    const voCh = heliNetwork.getCurrentVoiceChannel();
    if (voCh) {
      const channelSessionJwt = (
        await globalThis.heliport.v3.api.channelSessionApi.getChannelSessionJwt()
      ).data;
      if (!channelSessionJwt) {
        throw new Error('heliport.v3.api.channelSessionApi.getChannelSessionJwt() returns null.');
      }
      const decodeJwt = parseJwt(channelSessionJwt) as ChannelSession;
      // Smcnに関してのグループという概念がなくなったため、一旦コメントアウト
      // await this.#smcnJoinGroup(decodeJwt.position_channel_code ?? null, voCh.getChannelId() || '');
      this.#updateVoiceChannelMember();
      onNetVoiceConnected(decodeJwt.channel_session_code ?? null);
    }

    console.debug('Smcn joinVoiceRtc done');
  }
  /**
   * @param worldId nullの場合は、現在所属中のworldとなる(connect時に指定したworldIdと同じ)
   * @param groupId
   * @returns
   */
  async #smcnJoinGroup(worldId: string | null, groupId: string): Promise<void> {
    if (!this.#smcnClient) {
      console.error('[CHeliCarnelianSmcn] joinGroup: smcnClient is null');
      return;
    }
    try {
      // ユーザをグループに入れる
      await this.#smcnClient.joinGroup(worldId, groupId);
    } catch (error) {
      console.error(`[CHeliCarnelianSmcn] joinGroup: ${error}`);
    }
  }

  async initLocalMicInput(deviceId: string | null): Promise<boolean> {
    if (!this.#smcnClient) {
      console.error('[smcnClient] switchActiveDevice: smcnClient is null');
      window.alert(`[smcnClient] switchActiveDevice: smcnClient is null`);
      return false;
    }

    const result = await this.#smcnClient.switchActiveDevice('audioinput', deviceId); // change active device
    if (!result) {
      throw new Error(`Change Device Error`);
    }

    return true;
  }
  async changeMicDevice(deviceId: string): Promise<boolean> {
    if (this.#smcnWLocalTrackPublication) {
      const muted = this.#smcnWLocalTrackPublication.isMuted;
      const localMediaStream = await window.navigator.mediaDevices.getUserMedia({
        audio: {deviceId: deviceId ? {exact: deviceId} : undefined},
        video: false
      });
      const track = localMediaStream.getAudioTracks()[0];
      if (!track) throw new Error();
      this.#smcnWLocalTrackPublication.replaceTrack(track);

      closeLocalStream();
      setLocalStream(localMediaStream);

      this.#smcnWLocalTrackPublication.setMute(muted);
      if (this.getMicEnabled()) {
        // detectのmaxDomainが0になるため、止めてから再開する
        setMicEnabled(false);
        setMicEnabled(true);
      }
    }
    return true;
  }

  isConnected() {
    return this.#smcnClient?.isConnected ?? false;
  }

  connectRoomAfterPeerOpen(): void {}
}
